import React from 'react';
import { TabContentSkeleton } from './TabContentSkeleton';

interface SupportingStaffDetailSkeletonProps {
  /**
   * Number of tabs to display in the skeleton
   */
  tabCount?: number;

  /**
   * Additional class names
   */
  className?: string;
}

/**
 * SupportingStaffDetailSkeleton component
 *
 * This component provides a loading skeleton specifically designed for supporting staff detail pages.
 * It includes a profile header, tab navigation, and tab content.
 *
 * @example
 * <SupportingStaffDetailSkeleton />
 */
export const SupportingStaffDetailSkeleton: React.FC<
  SupportingStaffDetailSkeletonProps
> = ({ tabCount = 4, className = '' }) => {
  return (
    <div className={`min-h-screen bg-[#F5F8F9] ${className}`}>
      <div className="max-w-[1600px] mx-auto p-6 space-y-8">
        {/* Back Button Skeleton */}
        <div className="flex justify-start animate-pulse">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
            <div className="h-5 w-32 bg-gray-200 rounded-md"></div>
          </div>
        </div>

        {/* Profile Header Skeleton */}
        <div className="flex flex-wrap justify-between items-start animate-pulse">
          <div className="flex flex-wrap items-center gap-6">
            {/* Profile Image */}
            <div className="w-24 h-24 rounded-full bg-gray-200"></div>

            {/* Profile Info */}
            <div>
              <div className="h-10 w-64 bg-gray-200 rounded-md mb-2"></div>
              <div className="flex flex-wrap items-center gap-4 mt-2">
                <div className="h-5 w-24 bg-gray-100 rounded-md"></div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.5em] text-gray-300">•</span>
                  <div className="h-5 w-32 bg-gray-100 rounded-md"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-6">
            <div className="h-10 w-32 bg-gray-200 rounded-xl"></div>
          </div>
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="relative">
          <div className="flex items-center overflow-x-auto pb-1 border-b border-gray-100">
            {[...Array(tabCount)].map((_, index) => (
              <div
                key={index}
                className="h-10 rounded-md bg-gray-200 animate-pulse shrink-0 mr-2"
                style={{
                  width: `${80 + (index % 3) * 10}px`,
                  animationDelay: `${index * 0.1}s`,
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Tab Content Skeleton - using the existing TabContentSkeleton component */}
        <TabContentSkeleton
          sectionCount={2}
          rowsPerSection={6}
          withSidebar={true}
        />
      </div>
    </div>
  );
};

/**
 * SupportingStaffPersonalTabSkeleton component
 *
 * This component provides a loading skeleton specifically for the personal info tab
 * in the supporting staff detail page.
 */
export const SupportingStaffPersonalTabSkeleton: React.FC<{
  className?: string;
}> = ({ className = '' }) => {
  return (
    <div
      className={`bg-white rounded-2xl p-6 border border-[#E4E8EA] ${className}`}
    >
      <div className="animate-pulse">
        {/* Section Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="h-7 w-48 bg-gray-200 rounded-md"></div>
          <div className="h-9 w-24 bg-gray-200 rounded-lg"></div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-5 w-32 bg-gray-100 rounded-md"></div>
              <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>

        {/* Emergency Contact Section */}
        <div className="mt-8">
          <div className="h-7 w-48 bg-gray-200 rounded-md mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-5 w-32 bg-gray-100 rounded-md"></div>
                <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * SupportingStaffAttendanceTabSkeleton component
 *
 * This component provides a loading skeleton specifically for the attendance tab
 * in the supporting staff detail page.
 */
export const SupportingStaffAttendanceTabSkeleton: React.FC<{
  className?: string;
}> = ({ className = '' }) => {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Monthly Attendance Summary */}
      <div className="bg-white rounded-2xl p-6 border border-[#E4E8EA] animate-pulse">
        <div className="h-7 w-48 bg-gray-200 rounded-md mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4">
              <div className="h-6 w-24 bg-gray-200 rounded-md mb-2"></div>
              <div className="h-10 w-16 bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>

        {/* Attendance Chart */}
        <div className="mt-6 h-64 bg-gray-100 rounded-xl"></div>
      </div>

      {/* Leave Applications */}
      <div className="bg-white rounded-2xl p-6 border border-[#E4E8EA] animate-pulse">
        <div className="h-7 w-48 bg-gray-200 rounded-md mb-4"></div>

        {/* Leave Table */}
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Table Header */}
            <div className="grid grid-cols-5 gap-4 py-3 px-4 bg-gray-50 rounded-t-lg">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-6 bg-gray-200 rounded-md"></div>
              ))}
            </div>

            {/* Table Rows */}
            {[...Array(3)].map((_, rowIndex) => (
              <div
                key={rowIndex}
                className="grid grid-cols-5 gap-4 py-4 px-4 border-t border-gray-100"
                style={{ animationDelay: `${rowIndex * 0.05}s` }}
              >
                {[...Array(5)].map((_, colIndex) => (
                  <div
                    key={colIndex}
                    className="h-6 bg-gray-100 rounded-md"
                    style={{ width: colIndex === 4 ? '80%' : '90%' }}
                  ></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * SupportingStaffDocumentsTabSkeleton component
 *
 * This component provides a loading skeleton specifically for the documents tab
 * in the supporting staff detail page.
 */
export const SupportingStaffDocumentsTabSkeleton: React.FC<{
  className?: string;
}> = ({ className = '' }) => {
  return (
    <div
      className={`bg-white rounded-2xl p-6 border border-[#E4E8EA] animate-pulse ${className}`}
    >
      <div className="flex justify-between items-center mb-6">
        <div className="h-7 w-32 bg-gray-200 rounded-md"></div>
        <div className="h-9 w-36 bg-gray-200 rounded-lg"></div>
      </div>

      {/* Documents List */}
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between p-4 border border-gray-100 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gray-200 rounded-md"></div>
              <div>
                <div className="h-5 w-48 bg-gray-200 rounded-md mb-1"></div>
                <div className="h-4 w-32 bg-gray-100 rounded-md"></div>
              </div>
            </div>
            <div className="h-8 w-8 bg-gray-200 rounded-md"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * SupportingStaffTransportTabSkeleton component
 *
 * This component provides a loading skeleton specifically for the transport tab
 * in the supporting staff detail page.
 */
export const SupportingStaffTransportTabSkeleton: React.FC<{
  className?: string;
}> = ({ className = '' }) => {
  return (
    <div
      className={`bg-white rounded-2xl p-6 border border-[#E4E8EA] animate-pulse ${className}`}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="h-7 w-48 bg-gray-200 rounded-md mb-1"></div>
          <div className="h-5 w-32 bg-gray-100 rounded-md"></div>
        </div>
      </div>

      {/* Transport Type Selection */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded-xl"></div>
          ))}
        </div>

        {/* Transport Details */}
        <div className="mt-6 space-y-4">
          <div className="h-7 w-36 bg-gray-200 rounded-md"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-5 w-32 bg-gray-100 rounded-md"></div>
                <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
